import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalDialogComponent } from 'src/app/controls/components/advanced/modal-dialog/modal-dialog.component';
import { ButtonComponent } from 'src/app/controls/components/basic/button/button.component';
import { CheckboxComponent } from 'src/app/controls/components/basic/checkbox/checkbox.component';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { PasswordComponent, PasswordComponentValue, PasswordMode } from 'src/app/controls/components/basic/password/password.component';
import { RadiobuttonsComponent } from 'src/app/controls/components/basic/radiobuttons/radiobuttons.component';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { ButtonPrimaryDirective } from 'src/app/controls/directives/button-primary.directive';
import { ButtonSecondaryDirective } from 'src/app/controls/directives/button-secondary.directive';
import { OnboardingCredentials } from 'src/app/partnerportal/components/onboarding-credentials/models/credentials';
import { CredentialsStatus, Transport, TransportType } from 'src/app/partnerportal/models/partner-onboarding';
import { PartnerOnboardingTransportService } from '../../../../services/transport-service';

@Component({
  selector: 'app-sftp-transport-editor',
  standalone: true,
  imports: [ModalDialogComponent, FormsModule, TextboxComponent, RadiobuttonsComponent, CheckboxComponent, PasswordComponent, ButtonComponent, ButtonPrimaryDirective, ButtonSecondaryDirective, ModalDialogComponent, LayoutColumnComponent, LayoutRowComponent],
  templateUrl: './sftp-transport-editor.component.html',
  styleUrl: './sftp-transport-editor.component.scss'
})
export class SftpTransportEditorComponent {
  transportService = inject(PartnerOnboardingTransportService);
  private dialog = inject(NgbActiveModal);
  form = viewChild<NgForm>('form');

  transportOriginal = signal<Transport>(null);
  transportInternal = signal<Transport>(null);
  public dialogResult = signal<SftpTransportEditorDialogResult>(null);

  PasswordMode = PasswordMode;

  public set transport(transport: Transport) {
    this.transportOriginal.set(transport);
    this.transportInternal.set(transport);
  }

  result = computed(() => {
    const result: SftpTransportResult = {
      transport: this.transportInternal(),
      dialogResult: this.dialogResult()
    }
    return result;
  });

  credentials = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Credentials');
    const credentials = field?.Value ? JSON.parse(field.Value) as OnboardingCredentials : null;
    return credentials;
  });

  credentialsPassword = computed(() => {
    const credentials = this.credentials();
    const pcv: PasswordComponentValue = {
      id: credentials?.Password ?? null,
      value: null
    }
    return pcv;
  });

  hostname = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Hostname');
    const hostname = field?.Value;
    return hostname;
  });

  port = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'Port');
    const port = field?.Value;
    return port;
  });

  inboundFolder = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'InFolder');
    const inboundFolder = field?.Value;
    return inboundFolder;
  });

  outboundFolder = computed(() => {
    const field = this.transportInternal()?.TransportFields?.find(f => f.Name === 'OutFolder');
    const outboundFolder = field?.Value;
    return outboundFolder;
  });

  provideCredentialsNowChanged(provideNow: boolean): void {
    console.log('Provide credentials now changed: ', provideNow);
  }

  async typeChanged(newType: TransportType): Promise<void> {
    const originalType = this.transportOriginal()?.Type;
    if (originalType && newType && originalType !== newType) {
      const newTransport = await this.transportService.generateTransport(newType);
      this.transportInternal.set(newTransport);
    }
    else {
      this.transportInternal.set(this.transportOriginal());
    }
  }

  getTransportFromForm(): Transport {
    const type = this.form().form.get('type')?.value as string;
    const newTransportType = type === 'IntegratedSFTP' ? TransportType.IntegratedSFTP : TransportType.SFTPCustomerEndpoint;
    const oldTransportType = this.transportOriginal()?.Type;

    if (newTransportType === TransportType.IntegratedSFTP) {
      if (oldTransportType === TransportType.IntegratedSFTP) {
        // Return unmodified transport for integrated SFTP if type hasn't changed, as the form doesn't expose any editable fields for it
        return this.transportOriginal();
      }
      else {
        // For new integrated SFTP transport, return the generated transport with pre-filled hostname and folders, as the form doesn't expose any editable fields for it.
        // Note that credentials status is set to NotProvided for integrated SFTP, as credentials are not provided upfront and must be requested later by the partner.
        return new Transport({ ...this.transportInternal(), CredentialsStatus: CredentialsStatus.NotProvided });
      }
    }
    else {
      let credentials: OnboardingCredentials = null;
      const provideCredentialsNow = this.form().form.get('provideCredentialsNow')?.value as boolean;
      if (provideCredentialsNow) {
        const username = this.form().form.get('field-Username')?.value as string;
        const passwordComponentValue = this.form().form.get('field-Password')?.value as PasswordComponentValue;
        credentials = {
          Username: username,
          Password: passwordComponentValue?.value ? passwordComponentValue.value : passwordComponentValue?.id 
        };
      }

      // For non-integrated SFTP, return transport with values from the form, as the form exposes editable fields for hostname, credentials, inbound and outbound folders
      const updatedTransport = new Transport({
        ...this.transportInternal(),
        Type: newTransportType,
        CredentialsStatus: provideCredentialsNow && credentials ? CredentialsStatus.Provided : CredentialsStatus.NotProvided,
        TransportFields: this.transportInternal().TransportFields?.map(field => {
          if (field.Name.toLowerCase() === 'credentials') {
            return {
              ...field,
              Value: credentials ? JSON.stringify(credentials) : null
            };
          }
          const value = this.form().form.get('field-'+field.Name)?.value as string;
          return ({
            ...field,
            Value: value
          });
        }) ?? []
      });

      return updatedTransport;
    }
  }

  accept() {
    const updatedTransport = this.getTransportFromForm();
    this.transportInternal.set(updatedTransport);
    this.dialogResult.set(SftpTransportEditorDialogResult.Accept);
    this.dialog.close(this.result());
  }

  cancel() {
    this.dialogResult.set(SftpTransportEditorDialogResult.Cancel);
    this.dialog.close(this.result());
  }
}

export interface SftpTransportResult {
  transport: Transport;
  dialogResult: SftpTransportEditorDialogResult;
}

export enum SftpTransportEditorDialogResult {
  Accept,
  Cancel 
}
